import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ScheduleBooking,
  ScheduleSlot,
  cancelGuestManagedBooking,
  getGuestManagedBooking,
  getPublicBookingSlots,
  rescheduleGuestManagedBooking,
} from '../../api/client';
import { ArrowLeft, Calendar, CheckCircle2, Loader2, RefreshCw, Trash2 } from 'lucide-react';

export function GuestBookingManagePage() {
  const { magicToken } = useParams<{ magicToken: string }>();
  const [booking, setBooking] = useState<ScheduleBooking | null>(null);
  const [slots, setSlots] = useState<ScheduleSlot[]>([]);
  const [selectedStartAt, setSelectedStartAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!magicToken) {
      return;
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const bookingPayload = await getGuestManagedBooking(magicToken!);
        setBooking(bookingPayload);
        if (bookingPayload.publicSlug && bookingPayload.status === 'confirmed') {
          const available = await getPublicBookingSlots(bookingPayload.publicSlug, 21);
          setSlots(available);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load booking');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [magicToken]);

  const handleCancel = async () => {
    if (!magicToken) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await cancelGuestManagedBooking(magicToken!);
      setBooking(updated);
      setSuccess('Booking cancelled');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel booking');
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async () => {
    if (!magicToken || !selectedStartAt) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await rescheduleGuestManagedBooking(magicToken!, selectedStartAt);
      setBooking(updated);
      setSuccess('Booking rescheduled');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reschedule booking');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center px-4">
        <div className="max-w-xl app-panel border-danger-soft p-8">
          <h1 className="display-serif text-2xl text-ink">Booking not found</h1>
          <p className="mt-3 text-muted">{error || 'This management link is invalid or has expired.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-bg text-ink">
      <div className="max-w-4xl mx-auto px-4 py-8 md:px-6 md:py-12">
        <Link to="/" className="app-link inline-flex items-center gap-2 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" />
          Back to TrackWithAI
        </Link>

        <div className="mt-6 app-panel p-6 md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="display-serif text-3xl text-ink">Manage your booking</h1>
              <p className="mt-2 text-ink-soft">{booking.startLocal} at {booking.locationName}</p>
              <p className="text-sm text-muted mt-1">{booking.locationAddress}</p>
            </div>
            <div className="app-chip app-chip--brand">
              {booking.status}
            </div>
          </div>

          {(error || success) && (
            <div className={`${error ? 'error-banner' : 'success-banner'} mt-6`}>
              {error || success}
            </div>
          )}

          {booking.status === 'confirmed' && (
            <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_auto]">
              <div className="space-y-4">
                <div className="rounded-card border border-line bg-surface-strong p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-ink">
                    <Calendar className="w-4 h-4 text-brand" />
                    Reschedule
                  </div>
                  <p className="mt-2 text-sm text-ink-soft">Choose another open intro slot at this building.</p>
                  <select
                    className={inputClassName}
                    value={selectedStartAt}
                    onChange={(event) => setSelectedStartAt(event.target.value)}
                  >
                    <option value="">Select a new slot</option>
                    {slots.map((slot) => (
                      <option key={slot.startAt} value={slot.startAt}>{slot.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleReschedule}
                    disabled={!selectedStartAt || saving}
                    className="app-button app-button--primary app-button--compact mt-4"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Reschedule Booking
                  </button>
                </div>
              </div>

              <button
                onClick={handleCancel}
                disabled={saving}
                className="app-button app-button--compact app-button--danger-outline h-fit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Cancel Booking
              </button>
            </div>
          )}

          {booking.status !== 'confirmed' && (
            <div className="mt-6 rounded-card border border-line bg-surface-muted p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-ink">
                <CheckCircle2 className="w-4 h-4 text-success" />
                Booking status updated
              </div>
              <p className="mt-2 text-sm text-ink-soft">No further action is needed unless you want to book a new intro session later.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputClassName = 'app-input mt-4';
